<script setup lang="ts">
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { ref, watch } from 'vue';
  import FontIcon from '../../FontIcon/FontIcon.vue';
  import TextInput from '../TextInput/TextInput.vue';
  import type {
    PercentInputEmits,
    PercentInputProps,
  } from './PercentInput.types';

  const props = withDefaults(defineProps<PercentInputProps>(), {
    placeholder: '0',
    disabled: false,
    required: false,
  });

  const emit = defineEmits<PercentInputEmits>();

  const PERCENT_SCALE = 100;

  const toDisplay = (value: number | null | undefined) =>
    value === null || value === undefined
      ? ''
      : String(value / PERCENT_SCALE).replace('.', ',');

  const textInputRef = ref<InstanceType<typeof TextInput> | null>(null);

  const displayValue = ref(toDisplay(props.modelValue));

  watch(
    () => props.modelValue,
    (value) => {
      displayValue.value = toDisplay(value);
    },
  );

  const clamp = (value: number) => {
    let result = value;
    if (props.min !== undefined) {
      result = Math.max(result, props.min * PERCENT_SCALE);
    }
    if (props.max !== undefined) {
      result = Math.min(result, props.max * PERCENT_SCALE);
    }
    return result;
  };

  const parseToBaseValue = (raw: string): number | null => {
    const normalized = raw.replace(',', '.').trim();
    if (normalized === '') {
      return null;
    }
    const parsed = Number(normalized);
    if (Number.isNaN(parsed)) {
      return null;
    }
    return Math.round(parsed * PERCENT_SCALE);
  };

  const onUpdate = (value: string) => {
    const sanitized = value.replace(/[^\d.,]/g, '');
    displayValue.value = sanitized;
    const input = textInputRef.value?.inputRef;
    if (input && input.value !== sanitized) {
      input.value = sanitized;
    }
  };

  const onBlur = () => {
    const parsed = parseToBaseValue(displayValue.value);
    const value = parsed === null ? null : clamp(parsed);
    displayValue.value = toDisplay(value);
    emit('update:modelValue', value);
    emit('blur', value ?? 0, parsed ?? 0);
  };
</script>

<template>
  <TextInput
    :id="id"
    ref="textInputRef"
    :label="label"
    :helper-text="helperText"
    :error="error"
    :warning="warning"
    :name="name"
    :model-value="displayValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :disabled-reason="disabledReason"
    :required="required"
    :size="size"
    type="text"
    input-mode="decimal"
    @update:model-value="onUpdate"
    @blur="onBlur"
  >
    <template v-if="$slots.extraLabel" #extraLabel>
      <slot name="extraLabel" />
    </template>
    <template #icon>
      <FontIcon :icon-name="TUNDR_ICONS.PERCENT" />
    </template>
  </TextInput>
</template>
